import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import API from "../util/API";

/* =========================
   LOCAL STORAGE HELPERS
========================= */
const STORAGE_KEY = "user_settings";

const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to load settings from localStorage", error);
    return null;
  }
};

const saveToLocalStorage = (state) => {
  try {
    const settingsToSave = {
      theme: state.theme,
      language: state.language,
      currency: "USD",
      shipToCountry: state.shipToCountry,
      notifications: state.notifications,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
  } catch (error) {
    console.error("Failed to save settings to localStorage", error);
  }
};

/* =========================
   ASYNC THUNKS
========================= */
export const updateUserSettings = createAsyncThunk(
  "userSettings/updateUserSettings",
  async (settings) => {
    // Example API call (optional)
    // const response = await API.post("/user/settings", settings);
    // return response.data;
    return settings;
  }
);

export const userProfileInfo = createAsyncThunk(
  "userAuth/userProfileInfo",
  async (_, thunkAPI) => {
    try {
      const res = await API.get("/user/profile");
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data?.message);
    }
  }
);

/* =========================
   INITIAL STATE
========================= */
const savedSettings = loadFromLocalStorage();

const initialState = {
  userProfile: null,

  theme: savedSettings?.theme || "light",
  language: savedSettings?.language || "en",
  currency: "USD",
  shipToCountry: savedSettings?.shipToCountry || "BD",
  notifications:
    savedSettings?.notifications !== undefined
      ? savedSettings.notifications
      : true,

  status: "idle",
  error: null,
};

/* =========================
   SLICE
========================= */
const userSettingsSlice = createSlice({
  name: "userSettings",
  initialState,
  reducers: {
    setTheme(state, action) {
      state.theme = action.payload;
      saveToLocalStorage(state);
    },
    setLanguage(state, action) {
      state.language = action.payload;
      saveToLocalStorage(state);
    },
    setNotifications(state, action) {
      state.notifications = action.payload;
      saveToLocalStorage(state);
    },
    setShipToCountry(state, action) {
      state.shipToCountry = action.payload;
      saveToLocalStorage(state);
    },

    /* 🔥 MAIN SETTINGS UPDATE (used by SettingsPopup) */
    setSettingsSlice(state, action) {
      const { theme, language, shipToCountry, notifications } =
        action.payload;

      if (theme !== undefined) state.theme = theme;
      if (language !== undefined) state.language = language;
      state.currency = "USD";
      if (shipToCountry !== undefined)
        state.shipToCountry = shipToCountry;
      if (notifications !== undefined)
        state.notifications = notifications;

      saveToLocalStorage(state);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(updateUserSettings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateUserSettings.fulfilled, (state, action) => {
        state.status = "succeeded";
        Object.assign(state, action.payload);
        state.currency = "USD";
        saveToLocalStorage(state);
      })
      .addCase(updateUserSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      })
      .addCase(userProfileInfo.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userProfileInfo.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.userProfile = action.payload.data;
      })
      .addCase(userProfileInfo.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error.message;
      });
  },
});

/* =========================
   EXPORTS
========================= */
export const {
  setTheme,
  setLanguage,
  setNotifications,
  setShipToCountry,
  setSettingsSlice,
} = userSettingsSlice.actions;

export default userSettingsSlice.reducer;
